import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Check } from 'lucide-react'
import { adminApi, type ClientPortalSettings, type PortalOverrides } from '@/api/admin'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { PORTAL_FEATURES } from './portalFeatures'

type Choice = 'default' | 'on' | 'off'
const toChoice = (v: boolean | null): Choice => v === null ? 'default' : v ? 'on' : 'off'
const fromChoice = (c: Choice): boolean | null => c === 'default' ? null : c === 'on'

/** Client page -> Portal tab: override the tenant's portal defaults for this one client. */
export default function ClientPortalSettingsCard({ clientId }: { clientId: string }) {
  const queryClient = useQueryClient()
  const [draft, setDraft] = useState<PortalOverrides | null>(null)
  const [saved, setSaved] = useState(false)

  const { data } = useQuery({
    queryKey: ['client-portal-settings', clientId],
    queryFn: () => adminApi.getClientPortalSettings(clientId).then(r => r.data),
  })

  if (data && !draft) setDraft(data.overrides)

  const mutation = useMutation({
    mutationFn: () => adminApi.updateClientPortalSettings(clientId, draft!),
    onSuccess: () => {
      // Show what was just saved straight away rather than re-seeding the draft from the old cached data.
      queryClient.setQueryData<ClientPortalSettings>(['client-portal-settings', clientId], old => old && { ...old, overrides: draft! })
      queryClient.invalidateQueries({ queryKey: ['client-portal-settings', clientId] })
      setSaved(true)
      setTimeout(() => setSaved(false), 2500)
    },
  })

  if (!data || !draft) return <p className="text-sm text-gray-400">Loading…</p>

  const dirty = PORTAL_FEATURES.some(f => draft[f.key] !== data.overrides[f.key])
  const customised = PORTAL_FEATURES.some(f => data.overrides[f.key] !== null)

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base flex items-center gap-2">
          Client portal settings
          {customised && (
            <span className="text-[11px] font-semibold text-indigo-700 bg-indigo-50 border border-indigo-200 rounded-full px-2 py-0.5">
              Custom
            </span>
          )}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <p className="text-sm text-gray-500">
          What this client can do in their portal. "Use default" follows your company-wide setting
          (Settings → Client portal); choosing On or Off overrides it for this client only.
        </p>

        <div className="divide-y divide-gray-100 border border-gray-200 rounded-lg">
          {PORTAL_FEATURES.map(f => (
            <div key={f.key} className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 px-4 py-3">
              <div>
                <p className="text-sm font-medium text-gray-900">{f.label}</p>
                <p className="text-xs text-gray-500">{f.description}</p>
              </div>
              <select
                value={toChoice(draft[f.key])}
                onChange={e => setDraft({ ...draft, [f.key]: fromChoice(e.target.value as Choice) })}
                aria-label={f.label}
                className="border border-gray-200 rounded-lg px-3 py-1.5 text-sm bg-white shrink-0"
              >
                <option value="default">Use default ({data.defaults[f.key] ? 'On' : 'Off'})</option>
                <option value="on">Always on</option>
                <option value="off">Always off</option>
              </select>
            </div>
          ))}
        </div>

        <div className="flex items-center gap-3">
          <Button onClick={() => mutation.mutate()} disabled={!dirty || mutation.isPending}>
            {mutation.isPending ? 'Saving…' : 'Save'}
          </Button>
          {saved && <span className="flex items-center gap-1 text-sm text-green-600"><Check size={14} /> Saved</span>}
          {mutation.isError && <span className="text-sm text-red-600">Could not save.</span>}
        </div>
      </CardContent>
    </Card>
  )
}
